import React, { useState } from "react";
import { X, Loader2, Send, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

import { sendMarketUpdate } from "../api/prospectingReportsApi";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";

const TEAL = "#55c5d0";

// Split on commas, semicolons, whitespace or newlines so a list pasted out of a
// spreadsheet works without reformatting.
const parseRecipients = (text) =>
  Array.from(new Set(
    (text || "")
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes("@")),
  ));

/**
 * Send a market update to a list of addresses.
 *
 * The confirmation step is not ceremony: this fans out one email per address,
 * and the API returns 409 on a second send unless `force` is passed, so the
 * agent has to consciously choose to send twice.
 */
const SendMarketUpdateModal = ({ report, onClose, onSent }) => {
  const [raw, setRaw] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const recipients = parseRecipients(raw);
  const alreadySent = report?.status === "sent";

  const doSend = async (force = false) => {
    if (recipients.length === 0) {
      toast.error("Paste at least one email address.");
      return;
    }
    setSending(true);
    try {
      const { data } = await sendMarketUpdate(report.id, recipients, force ? { force: true } : {});
      toast.success(`Sent to ${data.sent} of ${data.attempted} recipient${data.attempted === 1 ? "" : "s"}.`);
      onSent(data.report);
    } catch (error) {
      if (error?.response?.status === 409) {
        setConfirming(true);
      } else {
        toast.error(error?.response?.data?.detail || "Could not send the market update.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Send market update</h2>
            <p className="text-xs text-gray-500">{report?.title}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {alreadySent && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                This update has already gone to {report.recipient_count} recipient
                {report.recipient_count === 1 ? "" : "s"}. Sending again will email them a second time.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Recipients
            </label>
            <textarea
              rows={5}
              autoFocus
              value={raw}
              onChange={(e) => { setRaw(e.target.value); setConfirming(false); }}
              placeholder="Paste email addresses, separated by commas, spaces or new lines."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              {recipients.length} valid address{recipients.length === 1 ? "" : "es"} detected.
              Duplicates are removed automatically.
            </p>
          </div>

          {/* What is actually going out. */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Preview</p>
            <div className="border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-900 mb-2">
                Subject: {report?.subject || report?.title}
              </p>
              <div
                className="prose prose-sm max-w-none text-xs text-gray-700"
                // Agent-authored HTML from a template any staff member can edit,
                // shown in this agent's session: sanitised so a script or
                // onerror= in someone else's template can't run here.
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(report?.body_html || "") }}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          {confirming ? (
            <button type="button" onClick={() => doSend(true)} disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-50">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Yes, send again
            </button>
          ) : (
            <button type="button" onClick={() => doSend(false)} disabled={sending || recipients.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
              style={{ backgroundColor: TEAL }}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to {recipients.length}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendMarketUpdateModal;
